import { uiText } from '@/i18n/interface';

import { toast } from "@/hooks/use-toast";

export interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  role: string; // Account type: 'student' or 'admin'

  // Admin Registration Code
  registrationCode?: string;
  registrationCodeValid?: boolean;

  // Dojo Setup (for admin signups)
  dojoName?: string;
  martialArtType?: string;
  martialArtStyle?: string;
  joinCode?: string;

  // Dojo Code (for student signups)
  dojoCode?: string;
  dojoCodeValid?: boolean;
  studentDojoName?: string;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;

  // Medical Information
  medicalConditions: string;
  currentMedications: string;
  allergies: string;
  injuries: string;
  doctorName: string;
  doctorPhone: string;

  // Martial Arts Background
  previousExperience: string;
  previousStyles: string;
  yearsOfExperience: string;
  previousBelt: string;

  // Preferences
  trainingGoals: string;
  howDidYouHear: string;

  // Custom country field
  customCountry: string;

  // Disclaimer
  disclaimerAgreed: boolean;
  digitalSignature: string;
  signatureDate: string;
}

export const validateStep = (step: number, formData: FormData): boolean => {
  const isAdminRole = formData.role === 'admin';

  switch (step) {
    case 1: // Personal Information
      if (!formData.firstName || !formData.lastName || !formData.dateOfBirth ||
          !formData.email || !formData.password || !formData.confirmPassword ||
          !formData.phone || !formData.address || !formData.country) {
        return false;
      }
      // If "Other" is selected, validate that customCountry is provided
      if (formData.country === 'Other' && !formData.customCountry) {
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: uiText("Password Mismatch"),
          description: uiText("Passwords do not match. Please check and try again."),
          variant: "destructive"
        });
        return false;
      }
      if (formData.password.length < 8) {
        toast({
          title: uiText("Password Too Short"),
          description: uiText("Password must be at least 8 characters long."),
          variant: "destructive"
        });
        return false;
      }
      return true;

    case 2: // Account Type Selection
      if (!formData.role) {
        return false;
      }
      // For admin role, no additional validation needed (registration codes removed)
      // For student role, no additional validation needed
      return true;

    case 3: // Dojo Setup (Admin) or Dojo Code (Student)
      if (isAdminRole) {
        // Dojo Setup validation
        return !!(formData.dojoName && formData.martialArtType && formData.martialArtStyle);
      }
      // For student role, dojo code is now required
      return !!(formData.dojoCode && formData.dojoCodeValid);

    case 4: // Emergency Contact
      return !!(formData.emergencyContactName && formData.emergencyContactPhone &&
               formData.emergencyContactRelationship);

    case 5: // Martial Arts Background
      return !!(formData.previousExperience && formData.trainingGoals);

    case 6: // Disclaimer
      return !!(formData.disclaimerAgreed && formData.digitalSignature);

    default:
      return true;
  }
};
